import { useEffect } from 'react';

export async function getCaptchaToken(action: string): Promise<string> {
    const siteKey = window.captchaSiteKey;
    const recaptcha = window.grecaptcha;

    if (!siteKey || !recaptcha) {
        throw new Error('reCAPTCHA is unavailable. Please try again.');
    }

    await new Promise<void>((resolve) => recaptcha.ready(resolve));
    const token = await recaptcha.execute(siteKey, { action });

    if (!token) {
        throw new Error('reCAPTCHA did not return a token. Please try again.');
    }

    return token;
}

export function CaptchaDomManager() {
    useEffect(() => {
        const handlers = new Map<
            HTMLFormElement,
            (event: SubmitEvent) => void
        >();

        const initForm = (form: HTMLFormElement) => {
            const isUmbracoForm =
                form.querySelector('input[name="FormId"]') !== null;

            if (
                form.dataset.captchaInitialized === 'true' ||
                form.dataset.skipCaptcha === 'true' ||
                isUmbracoForm
            ) {
                return;
            }

            form.dataset.captchaInitialized = 'true';
            let pending = false;
            let resuming = false;

            const handleSubmit = (event: SubmitEvent) => {
                if (
                    resuming ||
                    form.dataset.skipCaptcha === 'true' ||
                    form.querySelector('input[name="FormId"]') !== null
                ) {
                    return;
                }

                event.preventDefault();
                event.stopImmediatePropagation();

                if (pending) {
                    return;
                }

                pending = true;
                const submitter = event.submitter;

                void getCaptchaToken('submit')
                    .then((token) => {
                        if (!form.isConnected) {
                            return;
                        }

                        let hidden = form.querySelector<HTMLInputElement>(
                            "input[name='captchaToken']"
                        );

                        if (!hidden) {
                            hidden = document.createElement('input');
                            hidden.type = 'hidden';
                            hidden.name = 'captchaToken';
                            form.appendChild(hidden);
                        }

                        hidden.value = token;
                        resuming = true;

                        try {
                            form.requestSubmit(submitter ?? undefined);
                        } finally {
                            resuming = false;
                        }
                    })
                    .catch((error: unknown) =>
                        console.error('[CAPTCHA]', error)
                    )
                    .finally(() => {
                        pending = false;
                    });
            };

            // Capture the native event before React runs the form's onSubmit.
            form.addEventListener('submit', handleSubmit, true);
            handlers.set(form, handleSubmit);
        };

        const removeForm = (form: HTMLFormElement) => {
            const handler = handlers.get(form);
            if (handler) {
                form.removeEventListener('submit', handler, true);
                delete form.dataset.captchaInitialized;
                handlers.delete(form);
            }
        };

        document.querySelectorAll<HTMLFormElement>('form').forEach(initForm);

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.removedNodes.forEach((node) => {
                    if (node instanceof HTMLFormElement) {
                        removeForm(node);
                    } else if (node instanceof HTMLElement) {
                        node.querySelectorAll('form').forEach(removeForm);
                    }
                });

                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLFormElement) {
                        initForm(node);
                    } else if (node instanceof HTMLElement) {
                        node.querySelectorAll('form').forEach(initForm);
                    }
                });
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
            handlers.forEach((_, form) => removeForm(form));
        };
    }, []);

    return null;
}
